import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card';
import { Loader2, Sparkles } from 'lucide-react';
import type { ClassificationType } from '@/apps/finances/types/bankStatements';

const CLASSIFICATION_LABELS: Record<string, string> = {
  individual_receipt: 'Individual Receipt',
  psp_receipt: 'PSP Receipt',
  psp_receipt_stripe: 'PSP Receipt (Stripe)',
  psp_receipt_reduniq: 'PSP Receipt (Reduniq)',
  psp_receipt_monetico: 'PSP Receipt (Monetico)',
  psp_receipt_3pdm: 'PSP Receipt (3PDM)',
  client_payout: 'Client Payout',
  refund: 'Refund',
  gross_up_fee: 'Gross-Up Fee',
  bank_fee: 'Bank Fee',
  unclassified: 'Unclassified',
};

function confidenceVariant(confidence: number) {
  if (confidence >= 80) return 'success' as const;
  if (confidence >= 50) return 'warning' as const;
  return 'subtle' as const;
}

interface BankLineClassifyCardProps {
  bankLineId: string;
  suggestedType: ClassificationType;
  confidence: number;
  reasoning: string;
  onClassify: (lineId: string, classificationType: ClassificationType) => void;
  onDismiss: () => void;
  isClassifying?: boolean;
  confidenceLevel?: 'high' | 'medium-high' | 'medium' | 'low';
  matchedPattern?: string | null;
}

export function BankLineClassifyCard({
  bankLineId,
  suggestedType,
  confidence,
  reasoning,
  onClassify,
  onDismiss,
  isClassifying = false,
  confidenceLevel,
  matchedPattern,
}: BankLineClassifyCardProps) {
  return (
    <Card aria-label="Classification suggestion" className="py-3 bg-soft">
      <CardHeader className="flex items-center gap-2 px-3">
        <Sparkles className="size-3.5 text-brand-600 dark:text-brand-400 shrink-0" aria-hidden />
        <CardTitle>Suggested classification</CardTitle>
      </CardHeader>
      <CardContent className="px-3">
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-sm font-medium text-foreground">{CLASSIFICATION_LABELS[suggestedType] ?? suggestedType}</p>
            <Badge variant={confidenceVariant(confidence)} size="sm" className="shrink-0">
              {confidenceLevel
                ? `${confidenceLevel.charAt(0).toUpperCase() + confidenceLevel.slice(1)} · ${confidence}%`
                : `${confidence}%`}
            </Badge>
          </div>
          <p className="text-muted-foreground text-xs leading-relaxed">{reasoning}</p>
          {matchedPattern && (
            <p className="text-muted-foreground text-[11px]">
              Matched: <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">{matchedPattern}</code>
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() => onClassify(bankLineId, suggestedType)}
            disabled={isClassifying}
            className="shrink-0"
            aria-label={`Classify as ${CLASSIFICATION_LABELS[suggestedType] ?? suggestedType}`}
          >
            {isClassifying ? (
              <>
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
                Classifying...
              </>
            ) : (
              `Classify as ${CLASSIFICATION_LABELS[suggestedType] ?? suggestedType}`
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={onDismiss} disabled={isClassifying} aria-label="Dismiss suggestion">
            Dismiss
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
